// @flow
/** @jsx jsx */

import { jsx } from '@emotion/core';
import * as React from 'react';
import Button from 'react-bootstrap/lib/Button';
import Modal from 'react-bootstrap/lib/Modal';
import Glyphicon from 'react-bootstrap/lib/Glyphicon';
import InputGroup from 'react-bootstrap/lib/InputGroup';
import Well from 'react-bootstrap/lib/Well';
import clone from 'clone-deep';

import type { ColumnsMap, ColumMap } from '../../types';
import type { CommonActionProps } from './types.actions';
import ToggleController from '../../controllers/ToggleController';
import ButtonAction from './ButtonAction';
import Row from '../../components/Row';
import Col from '../../components/Col';
import { hideColumnsForNonRepUsers, isUserARepresentative } from '../../utils';
import { DesktopView, MobileView } from '../../config/mediaQuery';

type Props = {
  onColumnsChange: ColumnsMap => mixed,
  defaultColumnsMapping: ColumnsMap,
  columnsMapping: ColumnsMap,
  itemsPerColumn: number,
  collection: any,
};

type State = {
  changed: ColumnsMap,
  groupOfColumns: Array<ColumnsMap>,
};

class ColumnsAction extends React.Component<CommonActionProps & Props, State> {
  static defaultProps = {
    size: 'xsmall',
    itemsPerColumn: 10,
  };

  allowedColumns: ColumnsMap;

  constructor(props: CommonActionProps & Props) {
    super(props);

    const items = props.collection ? props.collection.items : [{}];

    this.allowedColumns = (isUserARepresentative(items)
      ? clone(props.defaultColumnsMapping)
      : hideColumnsForNonRepUsers(props.defaultColumnsMapping)
    );

    this.state = {
      changed: [],
      // this is for displaying columns of items instead of a plain list.
      // so unless specified otherwise, we'll be generating columns of
      // 10 items.
      groupOfColumns: this.allowedColumns.reduce((reducer, col) => {
        const index = reducer.length > 0 ? reducer.length - 1 : 0;
        const colItems = reducer[index];

        if (
          Array.isArray(colItems) &&
          colItems.length <= props.itemsPerColumn
        ) {
          colItems.push(col);
        } else {
          reducer.push([col]);
        }

        return reducer;
      }, []),
    };
  }

  onInputChange = (item: any) => {
    this.setState(state => {
      const pastItem = state.changed.find(i => i.binding === item.binding);

      // if it's the first time the item changes
      if (pastItem === undefined) {
        return {
          changed: [
            ...state.changed,
            {
              binding: item.binding,
              header: item.header,
              visible: item.visible === undefined ? false : !item.visible,
            },
          ],
        };
      }

      return {
        changed: state.changed.map(i => {
          if (i.binding === item.binding) {
            return {
              ...i,
              visible: i.visible === undefined ? false : !i.visible,
            };
          }

          return i;
        }),
      };
    });
  };

  onCloseAndDiscard = (onToggleVisibility: () => mixed) => {
    this.setState({
      changed: [],
    });

    onToggleVisibility();
  };

  onCloseAndSave = async (onToggleVisibility: () => mixed) => {
    if (this.state.changed.length > 0) {
      this.props.onColumnsChange([...this.state.changed]);

      this.setState({
        changed: [],
      });
    }

    onToggleVisibility();
  };

  renderItem = (col: ColumMap, checked: boolean) => (
    <InputGroup key={col.binding}>
      <label>
        <input
          type="checkbox"
          checked={checked}
          onChange={() => this.onInputChange(col)}
        />
        <span
          css={{
            paddingLeft: '5px',
          }}
        >
          {col.header}
        </span>
      </label>
    </InputGroup>
  );

  renderColumn = (defaultCol: ColumMap) => {
    if (!this.props.columnsMapping) return null;

    const userColumnMapping = this.props.columnsMapping.find(
      customCol => customCol.binding === defaultCol.binding,
    );

    if (userColumnMapping === undefined) {
      return this.renderItem(
        defaultCol,
        defaultCol.visible === undefined ? true : defaultCol.visible,
      );
    }

    const itemChanged = this.state.changed.find(
      item => item.binding === userColumnMapping.binding,
    );

    const isChecked =
      itemChanged !== undefined
        ? !!itemChanged.visible // `flow` complains without the casting.
        : userColumnMapping.visible === undefined ||
          userColumnMapping.visible === true;

    return this.renderItem(userColumnMapping, isChecked);
  };

  onSelectAll = () => {
    // check to avoid re-renders when all items are already selected.
    if (
      this.state.changed.length === this.allowedColumns.length &&
      !this.state.changed.some(item => item.visible !== true)
    ) {
      return;
    }

    this.setState(() => ({
      changed: this.allowedColumns.map(item => ({
        ...item,
        visible: true,
      })),
    }));
  };

  onSelectNone = () => {
    // check to avoid re-renders when all items are already deselected.
    if (
      this.state.changed.length === this.allowedColumns.length &&
      !this.state.changed.some(item => item.visible !== false)
    ) {
      return;
    }

    this.setState(() => ({
      changed: this.allowedColumns.map(item => ({
        ...item,
        visible: false,
      })),
    }));
  };

  render() {
    return (
      <ToggleController>
        {({ onToggleVisibility, visible }) => (
          <React.Fragment>
            <DesktopView>
              <ButtonAction
                bsSize={this.props.size}
                disabled={this.props.disabled}
                onClick={onToggleVisibility}
              >
                <Glyphicon glyph="th-list" /> Columns
              </ButtonAction>
            </DesktopView>
            <MobileView>
              <img
                src='assets/columns.svg'
                alt='columns icon'
                disabled={this.props.disabled}
                onClick={onToggleVisibility}
              />
            </MobileView>
            <DesktopView>
              <Modal
                show={visible}
                onHide={() => this.onCloseAndDiscard(onToggleVisibility)}
                bsSize="large"
              >
                <Modal.Header closeButton>
                  <Modal.Title>Column Picker</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Well>
                    <Button
                      onClick={this.onSelectAll}
                      bsSize="xsmall"
                      bsStyle="link"
                    >
                      Select All
                    </Button>{' '}
                    /{' '}
                    <Button
                      onClick={this.onSelectNone}
                      bsSize="xsmall"
                      bsStyle="link"
                    >
                      Select none
                    </Button>
                  </Well>
                  {visible && this.state.groupOfColumns.length > 0 && (
                    <Row
                      css={{
                        justifyContent: 'space-between',
                        maxWidth:
                          this.state.groupOfColumns.length === 2 ? '50%' : '100%',
                      }}
                    >
                      {this.state.groupOfColumns.map((column, index) => (
                        // eslint-disable-next-line
                        <Col key={index}>
                          {column.map(defaultCol =>
                            this.renderColumn(defaultCol),
                          )}
                        </Col>
                      ))}
                    </Row>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <Button onClick={() => this.onCloseAndSave(onToggleVisibility)}>
                    OK
                  </Button>
                  <Button
                    bsStyle="link"
                    onClick={() => this.onCloseAndDiscard(onToggleVisibility)}
                  >
                    Cancel
                  </Button>
                </Modal.Footer>
              </Modal>
            </DesktopView>
            <MobileView>
              <Modal
                show={visible}
                onHide={() => this.onCloseAndDiscard(onToggleVisibility)}
                bsSize="large"
                dialogClassName='columns-modal'
              >
                <Modal.Header>
                  <Modal.Title>Column Picker</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <div style={{ height: 44 }}>
                    <button
                      className='columns-top-button'
                      onClick={this.onSelectAll}
                    >
                      Select All
                    </button>{'    '}
                    <button
                      className='columns-top-button'
                      onClick={this.onSelectNone}
                    >
                      Select none
                    </button>
                  </div>
                  {visible && this.state.groupOfColumns.length > 0 && (
                    <Col
                      css={{
                        justifyContent: 'space-between',
                        width: '75vw'
                      }}
                    >
                      {this.state.groupOfColumns.map((column, index) => (
                        // eslint-disable-next-line
                        <Col key={index}>
                          {column.map(defaultCol =>
                            this.renderColumn(defaultCol),
                          )}
                        </Col>
                      ))}
                    </Col>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <div className='columns-bottom-button-group'>
                    <button
                      onClick={() => this.onCloseAndDiscard(onToggleVisibility)}
                    >
                      Cancel
                    </button>
                    <button onClick={() => this.onCloseAndSave(onToggleVisibility)}>
                      Show
                    </button>
                  </div>
                </Modal.Footer>
              </Modal>
            </MobileView>
          </React.Fragment>
        )}
      </ToggleController>
    );
  }
}

export default ColumnsAction;
